import app from 'flarum/forum/app';
import Page from 'flarum/common/components/Page';
import Link from 'flarum/common/components/Link';
import Button from 'flarum/common/components/Button';
import LogInModal from 'flarum/forum/components/LogInModal';
import extractText from 'flarum/common/utils/extractText';

import CodigoModal from './CodigoModal';
import InvitarPanel from './InvitarPanel';
import { seesN8nTag, N8N_TAG_SLUG } from '../utils/routeState';
import { canRequest, currentRequest, hasN8nAccess, requestPath } from '../utils/requestForm';
import { proposePath } from '../utils/proposeForm';
import n8nMark from '../utils/n8nMark';

/**
 * D7 — pagina publica del grupo n8n Chile.
 *
 * Es el gemelo cliente de `Umbral\Comunidad\Content\N8nChile`: el servidor rinde este mismo
 * contenido para el buscador y para quien llegue sin JS, y aqui Mithril lo reemplaza con la
 * version con sesion.
 *
 * Por que existe: `/t/n8n-chile` depende de una etiqueta restringida y devuelve 404 al
 * visitante anonimo (matriz de gaps T04 §1), de modo que la tarjeta n8n de la home y el
 * enlace del nav apuntaban a un 404. Esta ruta es publica por construccion y no consulta la
 * etiqueta para nada mas que decidir el CTA.
 *
 * T06 dejo esta pagina SIN el boton «Solicitar acceso» a proposito: el formulario M3 no
 * existia y un boton sin destino es peor que ninguno. T08 lo construye, asi que el boton entra
 * — y con el la promesa de la tarjeta «Como se entra» («Solicitud con tu cuenta Umbral») deja
 * de ser un callejon sin salida.
 */
export default class N8nChilePage extends Page {
  oninit(vnode: any) {
    super.oninit(vnode);

    app.setTitle(extractText(app.translator.trans('umbral-comunidad.forum.n8n_chile.meta_title')));
    app.setTitleCount(0);
  }

  view() {
    const t = (key: string) => app.translator.trans('umbral-comunidad.forum.n8n_chile.' + key);

    /*
      DOS PREGUNTAS DISTINTAS, Y CONFUNDIRLAS TIENE PRECEDENTE.

      «¿Tiene acceso?» es `hasN8nAccess()` — pertenencia al grupo O visibilidad efectiva de la
      categoria (ver el comentario de `utils/requestForm`, que explica por que hacen falta las
      dos y quien entra por cada una). Esta pagina fue la primera en calcularlo asi; desde
      PKG-FORO-UX-BUNDLE la formula vive en un solo sitio y la n8card del sidebar la comparte,
      que es lo que faltaba.

      «¿Puede ir a la categoria?» sigue siendo `seesN8nTag()` a secas, y esa pregunta NO se
      puede fundir con la anterior: T06-OPS encontro en produccion miembros del grupo sin el
      permiso de su propia categoria, y mandarlos a `/t/n8n-chile` es mandarlos a un 404.
    */
    const isMember = hasN8nAccess();
    const isGuest = !app.session.user;

    return (
      <div className="CmN8nPage-container container">
        <div className="CmN8nPage">
          <header className="CmN8nPage-head">
            <div>
              {/* T11 · decision A: simbolo oficial de n8n, no un icono dibujado a mano. Va
                  DENTRO del h1 y medido en `em` para que siga a la escala del titulo (que es
                  un `clamp`) sin una segunda media query. */}
              <h1 className="CmN8nPage-title">
                {n8nMark('cm-n8n-mark cm-n8n-mark--inline CmN8nPage-mark')} {t('title')}
              </h1>
              <p className="CmN8nPage-chip">
                <span className="cm-chip cm-closed">{t('chip')}</span>
              </p>
            </div>
            {this.cta(isMember, isGuest)}
          </header>

          <p className="CmN8nPage-intro">{t('intro')}</p>

          <div className="CmN8nPage-cards">
            <section className="CmN8nPage-card CmN8nPage-card--n8n">
              <h2>{t('rules_title')}</h2>
              <ul>
                <li>{t('rule_1')}</li>
                <li>{t('rule_2')}</li>
                <li>{t('rule_3')}</li>
              </ul>
            </section>
            <section className="CmN8nPage-card">
              <h2>{t('join_title')}</h2>
              {/* Titulo + explicacion por via (DELTA). Antes era una linea suelta cada una:
                  «Asistiendo a un encuentro (QR del evento)» decia por donde se entra pero no
                  que hacer con el QR ni que se obtiene al final. */}
              <p className="CmN8nPage-joinIntro">{t('join_intro')}</p>
              <ul className="CmN8nPage-ways">
                <li>
                  <strong>{t('join_1_title')}</strong> {t('join_1_body')}
                </li>
                <li>
                  <strong>{t('join_2_title')}</strong> {t('join_2_body')}
                </li>
                <li>
                  <strong>{t('join_3_title')}</strong> {t('join_3_body')}
                </li>
              </ul>
            </section>
          </div>

          <p className="CmN8nPage-note">{this.note(isMember, isGuest)}</p>

          {this.proposeCard(isMember)}

          {/* T12B · objetivo 3 — «invita a alguien», para quien ya está dentro.
              Se monta siempre y el propio panel decide si pintarse: la política la manda el
              servidor (`canInvite`/`reason`) y repartirla entre dos ficheros es como se
              desincroniza. Ver `InvitarPanel`. */}
          <InvitarPanel />
        </div>
      </div>
    );
  }

  /**
   * «Propon el proximo encuentro» — AQUI y no en la comunidad abierta.
   *
   * PKG-FORO-PRODUCT-GATES · decision C. Este bloque estaba en tres sitios abiertos (la home
   * del `custom_header`, el sidebar de `/t/encuentros` y la pagina `/encuentros`) y en ninguno
   * de n8n Chile. Los encuentros los organiza este grupo: la invitacion a proponer uno vive
   * donde estan las personas que los sostienen, no delante de quien acaba de llegar.
   *
   * SOLO PARA MIEMBROS, con la misma sonda que el resto de la pagina (`hasN8nAccess()`, ya
   * calculada en `view()`): al invitado y a quien todavia no tiene acceso esta pagina les esta
   * explicando como ENTRAR, y un segundo CTA compitiendo con ese es exactamente el ruido que la
   * decision viene a quitar.
   *
   * NO SE COMPRUEBA EL CORREO CONFIRMADO aqui: la pagina de destino ya tiene su estado «confirma
   * tu correo» con el reenvio (`ProponerPage`), que es mejor sitio para decirlo — ahi la persona
   * ya sabe que iba a hacer, y el aviso trae la accion que la desbloquea.
   */
  proposeCard(isMember: boolean) {
    if (!isMember) return null;

    const t = (key: string) => app.translator.trans('umbral-comunidad.forum.propose_card.' + key);

    return (
      <section className="CmProposeCard CmN8nPage-propose" data-cm-propose-card="n8n">
        <h3>{t('title')}</h3>
        <p>{t('body')}</p>
        <Link className="cm-btn-n8n CmProposeCard-cta" href={proposePath()}>
          {t('cta')}
        </Link>
      </section>
    );
  }

  /**
   * La nota de debajo acompana al CTA, y tiene que hablarle a quien la lee.
   *
   * `cta_guest_note` («primero la cuenta Umbral») es SOLO para el invitado. Con sesion hay tres
   * situaciones distintas y ninguna es esa: ya es miembro, ya solicito, o puede solicitar
   * ahora. La primera version de esta funcion caia en `cta_guest_note` en los dos ultimos
   * casos, de modo que a alguien con cuenta le decia que se hiciera una — y encima justo debajo
   * del boton «Solicitar acceso».
   */
  note(isMember: boolean, isGuest: boolean) {
    const t = (key: string) => app.translator.trans('umbral-comunidad.forum.n8n_chile.' + key);

    if (isMember) return t('member_note');
    if (isGuest) return t('cta_guest_note');
    if (currentRequest()?.status === 'pending') return t('request_note');

    // Con sesion, sin acceso y sin solicitud. Si la puerta no esta configurada tampoco hay
    // boton arriba, asi que la nota es la unica que puede explicar por que.
    return canRequest() ? t('member_request_note') : t('unavailable_note');
  }

  /**
   * CTA coherente con las puertas de la home (Spec §10: nadie se registra directo al grupo
   * cerrado; la puerta B lleva primero al alta Umbral).
   */
  cta(isMember: boolean, isGuest: boolean) {
    const t = (key: string) => app.translator.trans('umbral-comunidad.forum.n8n_chile.' + key);

    if (isMember) {
      // El enlace a la categoria solo si la sesion la VE de verdad: la pertenencia al grupo y
      // el permiso `tagN.viewForum` son cosas distintas (ver el comentario de `view()`), y
      // mandar a un miembro a `/t/n8n-chile` sin ese permiso es mandarlo a un 404.
      if (!seesN8nTag()) return null;

      return (
        <div className="CmN8nPage-cta">
          <Link className="cm-btn-n8n" href={app.route('tag', { tags: N8N_TAG_SLUG })}>
            {t('cta_member')}
          </Link>
        </div>
      );
    }

    if (!isGuest) {
      // Con sesion y sin acceso: el Form B (T08). `canRequest()` puede decir que no si un
      // admin todavia no eligio el grupo, y entonces NO se pinta el boton de solicitud — la
      // misma regla de honestidad de T06: nada de botones sin destino.
      //
      // El de «Ingresar código» si se pinta en los dos casos, porque su destino no depende del
      // grupo ni de la cola: quien tiene un codigo en la mano puede canjearlo aunque las
      // solicitudes esten cerradas, y justo entonces es su unica via.
      const pending = currentRequest()?.status === 'pending';

      return (
        <div className="CmN8nPage-cta">
          {this.codeButton()}
          {canRequest() ? (
            <Link className="cm-btn-n8n" href={requestPath()} data-cm-request-cta={pending ? 'pending' : 'open'}>
              {pending ? t('cta_request_pending') : t('cta_request')}
            </Link>
          ) : null}
        </div>
      );
    }

    /*
      T11 (D): el invitado va al Form A de `/registro`, no al modal nativo.

      Hasta aqui este boton abria `SignUpModal` — el alta de tres campos del core — porque en
      T06 `/registro` todavia no existia. Desde T09b si existe y es EL alta de la comunidad
      (identidad + acceso + perfil en un envio); mandar al modal desde D7 dejaba una cuenta sin
      perfil y contradecia la decision de producto del 2026-08-05. Con el alta ya publica (D),
      ademas, ese modal ya no aporta ni siquiera el campo de invitacion.

      Con el alta apagada en el panel (`allowSignUp` en false) no hay a donde mandar a nadie:
      se ofrece iniciar sesion, que es lo unico que si funciona.
    */
    if (!app.forum.attribute('allowSignUp')) {
      return (
        <div className="CmN8nPage-cta">
          {this.codeButton()}
          <Button className="Button Button--primary" onclick={() => app.modal.show(LogInModal)}>
            {t('cta_guest_login')}
          </Button>
        </div>
      );
    }

    return (
      <div className="CmN8nPage-cta">
        {this.codeButton()}
        <Link className="Button Button--primary" href={app.route('umbral-comunidad.registro')} data-cm-guest-cta="registro">
          {t('cta_guest')}
        </Link>
      </div>
    );
  }

  /**
   * «Ingresar código» — para quien ya trae uno de un encuentro o de una invitación.
   *
   * Va en TODOS los estados menos el de miembro: ahi no hay nada que canjear. Es el secundario
   * del par, asi que se queda con el `.Button` neutro del core y deja el acento (teal o
   * frambuesa) para la accion principal de cada estado.
   */
  codeButton() {
    return (
      <Button className="Button CmN8nPage-code" data-cm-code-cta="1" onclick={() => app.modal.show(CodigoModal)}>
        {app.translator.trans('umbral-comunidad.forum.n8n_chile.cta_code')}
      </Button>
    );
  }
}
